import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { addToBasket, removeFromBasket, subscribeBasketIds } from '../firebase/basketService';

// ── BASKET STATE ─────────────────────────────────────────────────────────────
// ONE SUBSCRIPTION FOR THE WHOLE APP. The basket button appears on every
// product in a lightbox and the count sits in the profile header; without a
// shared source each of those would open its own listener, and paging a
// lightbox would be a read per photo just to decide which icon to draw.
//
// It holds nothing but a Set of photo ids, which is all any of those need — the
// basket PAGE resolves the products themselves when it opens (see
// basketService for why the rows carry no product data).
const Ctx = createContext({
  ids: new Set(), count: 0, ready: false,
  has: () => false, toggle: async () => {},
});

export function BasketProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [ready, setReady] = useState(false);
  // Ids with a write in flight, so a second click cannot race the first.
  const [busy, setBusy] = useState(() => new Set());

  useEffect(() => {
    setIds(new Set());
    setReady(false);
    if (!user?.uid) return undefined;
    return subscribeBasketIds(user.uid, (next) => { setIds(next); setReady(true); });
  }, [user?.uid]);

  const has = useCallback((id) => ids.has(id), [ids]);

  /**
   * Adds or removes, returning what it did so a caller can react ('added' |
   * 'removed' | null when it did nothing).
   *
   * NOT OPTIMISTIC. The listener above is already live, so a successful write
   * repaints the button within a frame or two anyway — and an optimistic flip
   * would have to be unwound on failure, which for a two-state icon reads as it
   * toggling by itself. `busy` covers the gap instead.
   */
  const toggle = useCallback(async (item) => {
    const id = item?.id;
    if (!user?.uid || !id || busy.has(id)) return null;
    const adding = !ids.has(id);
    setBusy(prev => new Set(prev).add(id));
    try {
      if (adding) await addToBasket(user.uid, item);
      else await removeFromBasket(user.uid, id);
      return adding ? 'added' : 'removed';
    } catch {
      return null;
    } finally {
      setBusy(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [user?.uid, ids, busy]);

  const value = useMemo(() => ({
    ids,
    count: ids.size,
    ready,
    has,
    toggle,
    isBusy: (id) => busy.has(id),
  }), [ids, ready, has, toggle, busy]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useBasket = () => useContext(Ctx);
